/**
 * ELEMENT
 * a helper class for website elements
 * @author Arpad Hegedus <hegedus.arpad@gmail.com>
 * @version 1.0.0
 */

module.exports = class Element {
    constructor(selector) {
        (function () {
            if (typeof NodeList.prototype.forEach === "function") { return false; }
            NodeList.prototype.forEach = Array.prototype.forEach;
        })();

        this.elements = [];
        if (typeof selector === 'string') {
            this.elements = document.querySelectorAll(selector);
        } else if (typeof selector === 'object' && selector.nodeType === Node.ELEMENT_NODE) {
            this.elements = [selector];
        } else if (typeof selector === 'object' && 'elements' in selector) {
            this.elements = selector.elements;
        } else if (typeof selector === 'object' && typeof selector[0] === 'object') { 
            this.elements = selector;
        }
        this.singular = (this.elements.length === 1) ? true : false;
        this.empty = (this.elements.length === 0) ? true : false;
    }

    get height() { 
        if (this.empty) { return null; }
        let values = [];
        this.elements.forEach(el => {
            values.push(parseFloat(getComputedStyle(el).height));
        });
        if (this.singular) { return values[0]; }
        return values;
    }

    get width() {
        if (this.empty) { return null; }
        let values = [];
        this.elements.forEach(el => {
            values.push(parseFloat(getComputedStyle(el).width));
        });
        if (this.singular) { return values[0]; }
        return values;
    }

    get offsetTop() {
        if (this.empty) { return null; }
        let values = [];
        this.elements.forEach(el => {
            values.push(el.offsetTop);
        });
        if (this.singular) { return values[0]; }
        return values;
    }

    
    set background(value) { this.css('background', value); }
    set color(value) { this.css('color', value); }    
    set fontSize(value) { this.css('font-size', value); }
    set height(value) { this.css('height', value); }
    set minHeight(value) { this.css('min-height', value); }
    set maxHeight(value) { this.css('max-height', value); }
    set lineHeight(value) { this.css('line-height', value); }
    set padding(value) { this.css('padding', value); }
    set margin(value) { this.css('margin', value); }
    set textAlign(value) { this.css('text-align', value); }
    set width(value) { this.css('width', value); }
    set minWidth(value) { this.css('min-width', value); }
    set maxWidth(value) { this.css('max-width', value); }

    get top() { return this.offsetTop(); }

    get inViewport() {
        let inViewport = [];
        this.elements.forEach(el => {
            let e = new Element(el);
            if (e.isInViewport()) { 
                inViewport.push(e.node);
            }
        });
        return new Element(inViewport);
    }

    get node() {
        if (this.empty) { return undefined; }
        return this.elements[0];
    }

    get notInViewport() {
        let inViewport = [];
        this.elements.forEach(el => {
            let e = new Element(el);
            if (!e.isInViewport()) {
                inViewport.push(e.node);
            }
        });
        return new Element(inViewport);
    }

    get parent() { 
        let parents = [];
        if (this.empty) { return null; }
        this.elements.forEach(el => { 
            if (el.parentNode) {
                let alreadyAdded = false;
                parents.forEach(parent => { if (el.parentNode === parent) { alreadyAdded = true; } });
                if (!alreadyAdded) { parents.push(el.parentNode); }
            }
        });
        return new Element(parents);
    }

    get children() { 
        let children = [];
        if (this.empty) { return null; }
        this.elements.forEach(el => {
            if (el.childNodes) {
                el.childNodes.forEach(e => {
                    if (e.nodeType !== Node.TEXT_NODE) {
                        let alreadyAdded = false;
                        children.forEach(child => { if (e === child) { alreadyAdded = true; } });
                        if (!alreadyAdded) { children.push(e); }
                    }    
                });
            }
        });
        return new Element(children);
    }

    get siblings() {
        let siblings = [];
        if (this.empty) { return null; }
        this.elements.forEach(el => {
            if (el.parentNode.childNodes) {
                el.parentNode.childNodes.forEach(e => {
                    if (e.nodeType !== Node.TEXT_NODE) {
                        let alreadyAdded = false;
                        siblings.forEach(child => { if (e === child || e === el) { alreadyAdded = true; } });
                        if (!alreadyAdded) { siblings.push(e); }
                    }    
                });
            }
        });
        return new Element(siblings);
    }

    get next() { 
        let siblings = [];
        if (this.empty) { return null; }
        this.elements.forEach(el => {
            if (el.nextElementSibling) {
                let alreadyAdded = false;
                siblings.forEach(sibling => { if (el.nextElementSibling === sibling) { alreadyAdded = true; } });
                if (!alreadyAdded) { siblings.push(el.nextElementSibling); }
            }
        });
        return new Element(siblings);
    }

    get previous() {
        let siblings = [];
        if (this.empty) { return null; }
        this.elements.forEach(el => {
            if (el.previousElementSibling) {
                let alreadyAdded = false;
                siblings.forEach(sibling => { if (el.previousElementSibling === sibling) { alreadyAdded = true; } });
                if (!alreadyAdded) { siblings.push(el.previousElementSibling); }
            }
        });
        return new Element(siblings);
    }

    get(property) {
        if (this.empty) { return null; }
        let values = [];
        this.elements.forEach(el => {
            values.push(el[property]);
        });
        if (this.singular) { return values[0]; }
        return values;
    }

    set(property, value) {
        this.elements.forEach(el => {
            el[property] = value;
        });
        return this;
    }

    exists() { 
        return (this.empty) ? false : true;
    }

    addClass(className) { 
        this.elements.forEach(el => {
            if (el.classList) {
                el.classList.add(className);
            } else {
                let classes = el.className.split(' ');
                if (classes.indexOf(className) < 0) {
                    classes.push(className);
                }
                el.className = classes.join(' ');
            }
        });
        return this;
    }

    hasClass(className) {
        if (this.empty) { return null; }
        let values = [];
        this.elements.forEach(el => {
            if (el.classList) {
                values.push(el.classList.contains(className));
            } else {
                values.push(new RegExp('(^| )' + className + '( |$)', 'gi').test(el.className));
            }
        });
        if (this.singular) { return values[0]; }
        return values;
    }

    removeClass(className) {
        this.elements.forEach(el => {
            if (el.classList) {
                el.classList.remove(className);
                if (el.classList.value === '') { el.removeAttribute('class'); }
            } else {
                el.className = el.className.replace(new RegExp('(^|\\b' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
            }
        });
        return this;
    }

    toggleClass(className) {
        this.elements.forEach(el => {
            if (el.classList) {
                el.classList.toggle(className);
                if (el.classList.value === '') { el.removeAttribute('class'); }
            } else {
                let classes = el.className.split(' '),
                    existingIndex = classes.indexOf(className);
                if (existingIndex >= 0) {
                    classes.splice(existingIndex, 1);
                } else {
                    classes.push(className);
                }
                el.className = classes.join(' ');
            }
        });
        return this;
    }

    css(property, value) {
        property = property.split('-');
        property.forEach((v, i) => {
            if (i > 0) {
                property[i] = v.charAt(0).toUpperCase() + v.slice(1);
            }
        });
        property = property.join('');
        this.elements.forEach(el => {
            el.style[property] = value;
        });
        return this;
    }

    isInViewport(offsetY = 0, offsetX = 0) { 
        if (this.empty) { return null; }
        let values = [];
        this.elements.forEach(el => {
            let width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
                height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
                rect = el.getBoundingClientRect();
            rect = { top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left, width: rect.width, height: rect.height };
            if (offsetY !== 0) {
                if (typeof offsetY === 'string' && offsetY.indexOf('%') > 0) {
                    offsetY = parseInt(rect.height * parseFloat(offsetY.replace(/[^0-9\.\-]+/ig, '')) / 100);
                }
                offsetY = (typeof offsetY === 'string') ? parseInt(offsetY.replace(/[^0-9\.\-]+/ig, '')) : offsetY;
                rect.top = rect.top + offsetY;
                rect.bottom = rect.bottom + offsetY;
            }
            if (offsetX !== 0) {
                if (typeof offsetX === 'string' && offsetX.indexOf('%') > 0) {
                    offsetX = parseInt(rect.width * parseFloat(offsetX.replace(/[^0-9\.\-]+/ig, '')) / 100);
                }
                offsetX = (typeof offsetX === 'string') ? parseInt(offsetX.replace(/[^0-9\.\-]+/ig, '')) : offsetX;
                rect.left = rect.left + offsetX;
                rect.right = rect.right + offsetX;
            }
            values.push((rect.bottom > 0 && rect.right > 0 && rect.left < width && rect.top < height));
        });
        if (this.singular) { return values[0]; }
        return values;
    }

    equalHeights() { 
        if (this.empty) { return this; }
        let currentTallest = 0,
            currentRowStart = 0,
            rowDivs = [],
            el,
            topPosition = 0;
        this.elements.forEach(e => {
            el = new Element(e);
            topPosition = el.offsetTop;
            if (currentRowStart != topPosition) {
                let els = new Element(rowDivs);
                els.minHeight = `${currentTallest}px`;
                rowDivs = [];
                currentRowStart = topPosition;
                currentTallest = el.height;
                rowDivs.push(el.node);
            } else {
                rowDivs.push(el.node);
                currentTallest = (currentTallest < el.height) ? el.height : currentTallest;
            }
            let els = new Element(rowDivs);
            els.minHeight = `${currentTallest}px`;
        });
        return this;
    }

    getAttribute(attribute) { 
        let values = [];
        this.elements.forEach(el => { 
            values.push(el.getAttribute(attribute));
        });
        if (this.singular) { return values[0]; }
        return values;
    }

    setAttribute(attribute, value) {
        this.elements.forEach(el => {
            el.setAttribute(attribute, value);
        });
        return this;
    }

    listen(trigger, func, options = {}) {
        this.elements.forEach(el => { 
            el.addEventListener(trigger, e => { let args = [e, el]; func(...args); }, options);
        });
        return this;
    }

    unlisten(trigger, func, options = {}) { 
        this.elements.forEach(el => {
            el.removeEventListener(trigger, e => { let args = [e, el]; func(...args); }, options);
        });
    }

    addEventListener(trigger, func, options = {}) {
        return this.listen(trigger, func, options);
    }

    on(trigger, func, options = {}) {
        return this.listen(trigger, func, options);
    }

    forEach(func) {
        return this.each(func);
    }

    each(func) {
        this.elements.forEach((el, i) => {
            func(new Element(el), i, el);
        });
        return this;
    }

    eachStaggered(func, delay) {
        let time = 0;
        this.elements.forEach((el, i) => {
            window.setTimeout(() => {
                func(new Element(el), i, el);
            }, time);
            time = time + delay;
        });
        return this;
    }

    stagger(func, delay) { return this.eachStaggered(func, delay); }
}


// WEBPACK FOOTER //
// ./es6/modules/el.js